import type { InvoicingSettings } from '../hooks/useGetSettings';

export type InvoiceMismatchFlagFamily = 'amount' | 'quantity' | 'price';

const AMOUNT_ISSUE_TYPES = new Set<string>([
    'amount_mismatch',
    'amount_mismatch_multiple_nex_lines',
    'amount_mismatch_single_nex_line',
    'amount_mismatch_nex_structure',
]);

const QUANTITY_ISSUE_TYPES = new Set<string>(['quantity_mismatch']);

const PRICE_ISSUE_TYPES = new Set<string>([
    'price_mismatch',
    'list_price_mismatch',
    'list_price_equals_buy_price',
]);

export interface InvoiceMismatchFlagFamilyPolicy {
    amountMismatchFlaggingEnabled: boolean;
    quantityMismatchFlaggingEnabled: boolean;
    priceMismatchFlaggingEnabled: boolean;
}

/** Resolve toggles with default-on behavior for missing settings. */
export function resolveInvoiceMismatchFlagFamilyPolicy(
    invoicingSettings?: Pick<
        InvoicingSettings,
        | 'amountMismatchFlaggingEnabled'
        | 'quantityMismatchFlaggingEnabled'
        | 'priceMismatchFlaggingEnabled'
    > | null,
): InvoiceMismatchFlagFamilyPolicy {
    return {
        amountMismatchFlaggingEnabled: invoicingSettings?.amountMismatchFlaggingEnabled ?? true,
        quantityMismatchFlaggingEnabled: invoicingSettings?.quantityMismatchFlaggingEnabled ?? true,
        priceMismatchFlaggingEnabled: invoicingSettings?.priceMismatchFlaggingEnabled ?? true,
    };
}

export function getInvoiceMismatchFlagFamily(
    issueType: string | undefined,
): InvoiceMismatchFlagFamily | null {
    if (!issueType) return null;
    if (AMOUNT_ISSUE_TYPES.has(issueType)) return 'amount';
    if (QUANTITY_ISSUE_TYPES.has(issueType)) return 'quantity';
    if (PRICE_ISSUE_TYPES.has(issueType)) return 'price';
    return null;
}

export function isIssueTypeFlaggingEnabled(
    issueType: string | undefined,
    policy: InvoiceMismatchFlagFamilyPolicy,
): boolean {
    const family = getInvoiceMismatchFlagFamily(issueType);
    if (family === 'amount') return policy.amountMismatchFlaggingEnabled;
    if (family === 'quantity') return policy.quantityMismatchFlaggingEnabled;
    if (family === 'price') return policy.priceMismatchFlaggingEnabled;
    return true;
}

export function filterFlaggedPartsByPolicy<T extends { issueType: string }>(
    flaggedParts: T[] | undefined,
    policy: InvoiceMismatchFlagFamilyPolicy,
): T[] {
    return (flaggedParts ?? []).filter((part) => isIssueTypeFlaggingEnabled(part.issueType, policy));
}

/** Return slips are never amount-flagged. Other invoice types are unchanged. */
export function omitReturnSlipAmountFlags<T extends { issueType: string }>(
    flaggedParts: T[] | undefined,
    invoiceType: string | null | undefined,
): T[] {
    const parts = flaggedParts ?? [];
    if (invoiceType !== 'return_slip') return parts;
    return parts.filter((part) => getInvoiceMismatchFlagFamily(part.issueType) !== 'amount');
}
